import React, { useState } from "react";
import { Loader2, Check, PieChart } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const LABEL = { vehicle: "Privatanteil Fahrzeug", telecom: "Privatanteil Telefon", rent: "Privatanteil Homeoffice" };

/** Ein-Klick-Umbuchung eines Privatanteils (Privatkonto 2850 an Aufwand) mit vorgeschlagenem Prozentsatz. */
export function PrivateShareBooker({ companyId, tip, onBooked }) {
  const ps = tip.private_share;
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState(ps.suggested_percent);
  const [mode, setMode] = useState("percent");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(ps.booked ? { amount: null } : null);

  if (done) {
    return <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-medium text-emerald-800" data-testid={`private-share-booked-${tip.id}`}><Check className="h-3 w-3" />Privatanteil gebucht{done.amount != null && ` · ${chf(done.amount)}`}</span>;
  }
  const base = tip.amount || 0;
  const fixed = mode === "price" && price ? Math.round(Number(price) * 0.009 * 100) / 100 : null;
  const amount = mode === "price" ? fixed : Math.round(base * (Number(percent) || 0)) / 100;

  const book = async () => {
    setBusy(true);
    try {
      const body = { tip_id: tip.id, year: ps.year, month: ps.month, label: LABEL[ps.category] || "Privatanteil" };
      if (mode === "price") body.fixed_amount = fixed; else body.percent = Number(percent);
      const r = await apiClient.post(`/companies/${companyId}/simple-ledger/private-share`, body);
      setDone({ amount: r.data.amount });
      toast.success(`${r.data.description}: ${chf(r.data.amount)} auf ${r.data.private_account} gebucht`);
      onBooked?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-700 px-3 py-1 text-[11px] font-medium text-white hover:bg-amber-800 transition-colors" data-testid={`private-share-open-${tip.id}`}>
        <PieChart className="h-3 w-3" />Privatanteil buchen ({ps.suggested_percent} % vorgeschlagen)
      </button>
    );
  }
  return (
    <div className="mt-2 rounded-md border border-amber-300 bg-white/80 p-2.5 text-[11px] text-slate-800 space-y-2" data-testid={`private-share-form-${tip.id}`}>
      <div className="text-slate-600">{ps.basis}</div>
      {ps.monthly_from_price && (
        <div className="flex gap-3">
          <label className="inline-flex items-center gap-1"><input type="radio" checked={mode === "percent"} onChange={() => setMode("percent")} data-testid={`private-share-mode-percent-${tip.id}`} />% der Monatskosten</label>
          <label className="inline-flex items-center gap-1"><input type="radio" checked={mode === "price"} onChange={() => setMode("price")} data-testid={`private-share-mode-price-${tip.id}`} />0.9 % des Kaufpreises</label>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {mode === "percent" ? (
          <>
            <input type="number" min="1" max="100" step="1" value={percent} onChange={(e) => setPercent(e.target.value)} className="w-16 rounded border border-slate-300 px-1.5 py-1 text-right" data-testid={`private-share-percent-${tip.id}`} />
            <span>% von {chf(base)}</span>
          </>
        ) : (
          <>
            <span>Kaufpreis</span>
            <input type="number" min="0" step="100" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="z. B. 35000" className="w-24 rounded border border-slate-300 px-1.5 py-1 text-right" data-testid={`private-share-price-${tip.id}`} />
            <span>→ 0.9 % pro Monat</span>
          </>
        )}
        <span className="font-semibold tabular-nums" data-testid={`private-share-amount-${tip.id}`}>= {amount != null ? chf(amount) : "–"}</span>
      </div>
      <div className="text-slate-500">Buchung per Monatsende: Privatkonto 2850 (Soll) an {Object.keys(ps.base_by_account).join(", ")} (Haben)</div>
      <div className="flex gap-2">
        <button type="button" onClick={book} disabled={busy || !amount} className="rounded-full bg-slate-900 px-3 py-1 font-medium text-white hover:bg-slate-700 disabled:opacity-50" data-testid={`private-share-submit-${tip.id}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Jetzt buchen"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="rounded-full px-3 py-1 text-slate-600 hover:bg-slate-100" data-testid={`private-share-cancel-${tip.id}`}>Abbrechen</button>
      </div>
    </div>
  );
}
